/**
 * RFC-style delimited-line parser (quoted fields, doubled quotes). Shared for CSV/TSV imports.
 * Supports both comma-separated (CSV) and tab-separated (TSV) input so users can paste
 * directly from Excel / Google Sheets without exporting a file first.
 */

/**
 * @param {string} line
 * @param {string} [delimiter=","] - single-character field delimiter (",", "\t", ";", "|")
 * @returns {string[]}
 */
export function parseCsvLine(line, delimiter = ",") {
  const values = [];
  let current = "";
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    if (char === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (char === delimiter && !inQuotes) {
      values.push(current.trim());
      current = "";
    } else {
      current += char;
    }
  }
  values.push(current.trim());
  return values;
}

/**
 * Detect the field delimiter of a delimited-text document. Prefers tab (Excel/Sheets paste),
 * then comma, then semicolon / pipe. Falls back to comma when nothing is found.
 * @param {string} csvText
 * @returns {string} one of "\t", ",", ";", "|"
 */
export function detectCsvDelimiter(csvText) {
  const firstLine = String(csvText || "")
    .split(/\r?\n/)
    .find((line) => line.trim() !== "");
  if (!firstLine) return ",";
  // Count only delimiters outside quoted fields on the header line.
  const counts = { "\t": 0, ",": 0, ";": 0, "|": 0 };
  let inQuotes = false;
  for (let i = 0; i < firstLine.length; i++) {
    const char = firstLine[i];
    if (char === '"') {
      if (inQuotes && firstLine[i + 1] === '"') i++;
      else inQuotes = !inQuotes;
    } else if (!inQuotes && counts[char] !== undefined) {
      counts[char] += 1;
    }
  }
  const preference = ["\t", ",", ";", "|"];
  let best = ",";
  let bestCount = 0;
  for (const d of preference) {
    if (counts[d] > bestCount) {
      best = d;
      bestCount = counts[d];
    }
  }
  return bestCount > 0 ? best : ",";
}

function normalizeHeaderCell(h) {
  return h.replace(/^"|"$/g, "").trim().toLowerCase().replace(/\s+/g, "_");
}

/**
 * Parse CSV or TSV text into a normalized header/rows table. Delimiter is auto-detected
 * (tab vs comma vs semicolon/pipe) unless one is supplied.
 * @param {string} csvText
 * @param {string} [delimiter] - force a delimiter; auto-detected when omitted
 * @returns {{ headers: string[], rows: string[][] }}
 */
export function parseCsvTextToNormalizedTable(csvText, delimiter) {
  const lines = csvText.split(/\r?\n/).filter((line) => line.trim() !== "");
  if (lines.length < 2) {
    throw new Error("CSV must have at least a header row and one data row");
  }
  const sep = delimiter || detectCsvDelimiter(csvText);
  const headers = parseCsvLine(lines[0], sep).map(normalizeHeaderCell);
  const rows = [];
  for (let i = 1; i < lines.length; i++) {
    rows.push(parseCsvLine(lines[i], sep).map((v) => v.replace(/^"|"$/g, "").trim()));
  }
  return { headers, rows };
}

/**
 * @param {string[]} headers - normalized header names
 * @param {string[]} candidates - normalized names to try in order
 * @returns {number}
 */
export function findCsvColumnIndex(headers, candidates) {
  for (let c = 0; c < candidates.length; c++) {
    const key = candidates[c].toLowerCase().replace(/\s+/g, "_");
    const idx = headers.indexOf(key);
    if (idx >= 0) return idx;
  }
  return -1;
}

/**
 * Safe trimmed cell read by column index (missing/negative index → "").
 * @param {string[]} values
 * @param {number} index
 * @returns {string}
 */
export function csvCellAt(values, index) {
  if (index < 0 || !values || values[index] === undefined) return "";
  return String(values[index] ?? "").trim();
}

/**
 * Rejoin US currency cells split by unquoted thousands commas (e.g. "$81" + "341.49" → "$81,341.49").
 * Quoted "$81,341.49" is unchanged. Safe to run on every CSV row.
 * @param {string[]} values
 * @returns {string[]}
 */
export function mergeSplitCurrencyCells(values) {
  if (!Array.isArray(values) || values.length === 0) return values;
  let cells = values.map((v) => (v != null ? String(v) : ""));
  let changed = true;
  while (changed) {
    changed = false;
    const next = [];
    for (let i = 0; i < cells.length; i++) {
      const cur = cells[i].trim();
      const n1 = (cells[i + 1] ?? "").trim();
      const n2 = (cells[i + 2] ?? "").trim();
      if (/^\$[\d,]+$/.test(cur) && /^\d+\.\d+$/.test(n1)) {
        next.push(`${cur},${n1}`);
        i += 1;
        changed = true;
      } else if (/^\$[\d]+$/.test(cur) && /^\d{1,3}$/.test(n1) && /^\d{2}$/.test(n2)) {
        next.push(`${cur},${n1}.${n2}`);
        i += 2;
        changed = true;
      } else {
        next.push(cells[i]);
      }
    }
    cells = next;
  }
  return cells;
}
